import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";
import { apiFetch } from "@/lib/api";
import { useRequireAuth } from "@/hooks/useRequireAuth";

// MK_NUDGE_V1: short encouragement under the progress bar so people finish all questions
const NUDGES = {
  english: {
    start: "Answer from the gut. Your first answer is usually the true one.",
    early: "Good start. Your mind map is taking shape.",
    mid: "Your strongest houses are already showing up.",
    half: "Halfway there. Your pattern is starting to appear.",
    near: (n) => `Just ${n} left to find what is really holding you back.`,
    last: "Last one. Your Mind Kundali is next.",
    done: "All done. Submit to see your Mind Kundali.",
    left: (n) => `${n} left`,
  },
  hinglish: {
    start: "Dil se jawab dijiye. Pehla jawab hi aksar sach hota hai.",
    early: "Achhi shuruaat. Aapka mind map ban raha hai.",
    mid: "Aapke strong houses dikhne lage hain.",
    half: "Aadha safar poora. Aapka pattern dikhne laga hai.",
    near: (n) => `Bas ${n} aur, phir pata chalega asli rukawat kya hai.`,
    last: "Bas ek aur. Phir aapki Mind Kundali.",
    done: "Ho gaya! Submit kijiye aur apni Mind Kundali dekhiye.",
    left: (n) => `${n} baaki`,
  },
  hindi: {
    start: "दिल से जवाब दीजिए। पहला जवाब अक्सर सबसे सच्चा होता है।",
    early: "अच्छी शुरुआत। आपका माइंड मैप बन रहा है।",
    mid: "आपके मज़बूत घर दिखने लगे हैं।",
    half: "आधा सफ़र पूरा। आपका पैटर्न दिखने लगा है।",
    near: (n) => `बस ${n} और, फिर पता चलेगा असली रुकावट क्या है।`,
    last: "बस एक और। फिर आपकी माइंड कुंडली।",
    done: "हो गया! सबमिट कीजिए और अपनी माइंड कुंडली देखिए।",
    left: (n) => `${n} बाकी`,
  },
};

function nudgeFor(answered, total, lang) {
  const t = NUDGES[lang] || NUDGES.english;
  const left = Math.max(total - answered, 0);
  const p = total ? answered / total : 0;
  if (total && left === 0) return { stage: "done", text: t.done, left: t.left(0) };
  if (left === 1) return { stage: "last", text: t.last, left: t.left(1) };
  if (answered === 0) return { stage: "start", text: t.start, left: t.left(left) };
  if (p < 0.25) return { stage: "early", text: t.early, left: t.left(left) };
  if (p < 0.5) return { stage: "mid", text: t.mid, left: t.left(left) };
  if (p < 0.75) return { stage: "half", text: t.half, left: t.left(left) };
  return { stage: "near", text: t.near(left), left: t.left(left) };
}

function NudgeLine({ nudge }) {
  return (
    <div style={{ marginBottom: "1.25rem", minHeight: "1.6em" }}>
      <style>{`@keyframes mkNudgeIn{from{opacity:0;transform:translateY(4px)}to{opacity:1;transform:none}}`}</style>
      <p
        key={nudge.stage}
        data-testid="assessment-nudge"
        className="serif"
        style={{ margin: 0, textAlign: "center", fontStyle: "italic", fontSize: "1.05rem", lineHeight: 1.4, color: "#D4AF37", animation: "mkNudgeIn .45s ease both" }}
      >
        {nudge.text}
      </p>
    </div>
  );
}

const OPTION_LETTERS = ["A", "B", "C", "D"];

function Centered({ children }) {
  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "2rem",
        background: "radial-gradient(circle at 50% 30%, #14102A 0%, #03030A 70%)",
        color: "#F4F6F0",
        fontFamily: "Outfit, system-ui, sans-serif",
      }}
    >
      <div className="mandala-watermark" />
      <div className="grain-overlay" />
      <div style={{ position: "relative", zIndex: 3, width: "100%", maxWidth: 640 }}>{children}</div>
    </div>
  );
}

function LanguagePills({ current, labels, onChange }) {
  if (!labels || Object.keys(labels).length < 2) return null;
  return (
    <div
      style={{
        display: "flex",
        gap: "0.5rem",
        justifyContent: "center",
        marginBottom: "1.25rem",
        flexWrap: "wrap",
      }}
    >
      {Object.entries(labels).map(([key, label]) => {
        const active = key === current;
        return (
          <button
            key={key}
            onClick={() => onChange(key)}
            style={{
              padding: "0.4rem 1rem",
              borderRadius: 20,
              border: active ? "1px solid #D4AF37" : "1px solid rgba(255,255,255,0.15)",
              background: active ? "rgba(212, 175, 55, 0.15)" : "transparent",
              color: active ? "#D4AF37" : "#A0A0B0",
              cursor: "pointer",
              fontFamily: "Outfit, sans-serif",
              fontSize: "0.82rem",
              fontWeight: active ? 600 : 400,
              transition: "all 0.2s",
            }}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}

export default function Assessment() {
  const { session } = useRequireAuth();

  const [phase, setPhase] = useState("loading");
  const [loadError, setLoadError] = useState("");
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [index, setIndex] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  // Language state — persisted in localStorage so it survives a page refresh
  const [lang, setLang] = useState(() => localStorage.getItem("mk_lang") || "hinglish");
  const [availableLanguages, setAvailableLanguages] = useState({});

  const loadQuestions = async (language) => {
    try {
      const data = await apiFetch(`/assessment/questions?lang=${language}`, { auth: true });
      setQuestions(data.questions || []);
      if (data.available_languages) setAvailableLanguages(data.available_languages);
      return true;
    } catch (err) {
      setLoadError(err.message || "Couldn't load questions.");
      return false;
    }
  };

  useEffect(() => {
    if (!session?.session_token) return;
    (async () => {
      try {
        const me = await apiFetch("/me", { auth: true });
        if (me.assessment_completed) {
          setPhase("already_done");
          return;
        }
        await loadQuestions(lang);
        setPhase("in_progress");
      } catch (err) {
        setLoadError(err.message || "Couldn't load your assessment. Please refresh and try again.");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session]);

  const handleLanguageChange = async (newLang) => {
    // Answers are keyed by question ID (numbers), which don't change across
    // languages — so switching language preserves all answers automatically.
    setLang(newLang);
    localStorage.setItem("mk_lang", newLang);
    await loadQuestions(newLang);
  };

  const total = questions.length;
  const current = questions[index];
  const answeredCount = useMemo(() => Object.keys(answers).length, [answers]);
  const allAnswered = total > 0 && answeredCount >= total;

  const selectOption = (questionId, letter) => {
    setAnswers((prev) => ({ ...prev, [questionId]: letter }));
  };

  const goNext = () => {
    if (index < total - 1) setIndex((i) => i + 1);
  };
  const goBack = () => {
    if (index > 0) setIndex((i) => i - 1);
  };

  const handleSubmit = async () => {
    if (submitting || !allAnswered) return;
    setSubmitError("");
    setSubmitting(true);
    try {
      const numericAnswers = {};
      Object.entries(answers).forEach(([qid, letter]) => {
        numericAnswers[Number(qid)] = letter;
      });
      await apiFetch("/assessment/submit", {
        method: "POST",
        auth: true,
        body: { answers: numericAnswers, origin_url: window.location.origin },
      });
      setPhase("submitted");
    } catch (err) {
      setSubmitError(err.message || "Something went wrong submitting your answers. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loadError) {
    return (
      <Centered>
        <p style={{ color: "#E0115F" }}>{loadError}</p>
      </Centered>
    );
  }

  if (phase === "loading") {
    return (
      <Centered>
        <div className="pulse-dot" style={{ width: 10, height: 10, borderRadius: "50%", background: "#D4AF37" }} />
      </Centered>
    );
  }

  if (phase === "already_done") {
    return (
      <Centered>
        <div className="glass-panel" style={{ padding: "2.5rem", borderRadius: 22, textAlign: "center" }}>
          <CheckCircle2 size={40} style={{ color: "#50C878" }} />
          <h1 className="serif" style={{ fontSize: "1.8rem", margin: "1rem 0 0.6rem", fontWeight: 500 }}>
            You've already completed your assessment
          </h1>
          <p style={{ color: "#A0A0B0", marginBottom: "1.8rem" }}>
            Head to your dashboard to check on your report.
          </p>
          <Link to="/dashboard" className="cta-button">Go to dashboard</Link>
        </div>
      </Centered>
    );
  }

  if (phase === "submitted") {
    return (
      <Centered>
        <div className="glass-panel" style={{ padding: "2.5rem", borderRadius: 22, textAlign: "center" }}>
          <CheckCircle2 size={40} style={{ color: "#50C878" }} />
          <h1 className="serif" style={{ fontSize: "1.8rem", margin: "1rem 0 0.6rem", fontWeight: 500 }}>
            Your Mind Kundli is being drawn
          </h1>
          <p style={{ color: "#A0A0B0", marginBottom: "1.8rem", lineHeight: 1.6 }}>
            We'll email you once it's ready. You can also check progress any time from your dashboard.
          </p>
          <Link to="/dashboard" className="cta-button">Go to dashboard</Link>
        </div>
      </Centered>
    );
  }

  // in_progress
  if (!current) return null;

  return (
    <Centered>
      <LanguagePills current={lang} labels={availableLanguages} onChange={handleLanguageChange} />

      <div style={{ marginBottom: "1.5rem" }}>
        <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.8rem", color: "#A0A0B0", marginBottom: "0.5rem" }}>
          <span>Question {index + 1} of {total}</span>
          <span>{nudgeFor(answeredCount, total, lang).left}</span>
        </div>
        <div style={{ height: 4, borderRadius: 2, background: "rgba(255,255,255,0.08)", overflow: "hidden" }}>
          <div
            style={{
              height: "100%",
              width: `${((index + 1) / total) * 100}%`,
              background: "linear-gradient(90deg, #D4AF37, #F0D471)",
              transition: "width 0.3s ease",
            }}
          />
        </div>
      </div>

      <NudgeLine nudge={nudgeFor(answeredCount, total, lang)} />
      <div className="glass-panel" style={{ padding: "2.25rem", borderRadius: 22 }} data-testid="assessment-question-card">
        <h2 className="serif" style={{ fontSize: "1.4rem", fontWeight: 500, lineHeight: 1.4, marginBottom: "1.75rem" }}>
          {current.text}
        </h2>

        <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
          {OPTION_LETTERS.map((letter) => {
            const optionText = current.options?.[letter];
            if (!optionText) return null;
            const selected = answers[current.id] === letter;
            return (
              <button
                key={letter}
                type="button"
                data-testid={`assessment-option-${letter}`}
                onClick={() => selectOption(current.id, letter)}
                style={{
                  textAlign: "left",
                  padding: "0.9rem 1.1rem",
                  borderRadius: 12,
                  border: selected ? "1px solid #D4AF37" : "1px solid rgba(255,255,255,0.1)",
                  background: selected ? "rgba(212, 175, 55, 0.12)" : "rgba(255,255,255,0.02)",
                  color: "#F4F6F0",
                  cursor: "pointer",
                  fontFamily: "Outfit, sans-serif",
                  fontSize: "0.92rem",
                  lineHeight: 1.5,
                  display: "flex",
                  gap: "0.75rem",
                  alignItems: "flex-start",
                  transition: "border-color 0.2s, background 0.2s",
                }}
              >
                <span
                  style={{
                    flexShrink: 0,
                    width: 24,
                    height: 24,
                    borderRadius: "50%",
                    border: selected ? "1px solid #D4AF37" : "1px solid rgba(255,255,255,0.25)",
                    color: selected ? "#D4AF37" : "#A0A0B0",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "0.75rem",
                  }}
                >
                  {letter}
                </span>
                <span>{optionText}</span>
              </button>
            );
          })}
        </div>

        {submitError && (
          <p style={{ color: "#E0115F", fontSize: "0.85rem", marginTop: "1.25rem" }}>{submitError}</p>
        )}

        <div style={{ display: "flex", justifyContent: "space-between", marginTop: "2rem" }}>
          <button
            type="button"
            onClick={goBack}
            disabled={index === 0}
            className="ghost-button"
            style={{ opacity: index === 0 ? 0.4 : 1 }}
          >
            Back
          </button>

          {index < total - 1 ? (
            <button
              type="button"
              onClick={goNext}
              disabled={!answers[current.id]}
              className="cta-button"
              style={{ opacity: !answers[current.id] ? 0.5 : 1 }}
            >
              Next
            </button>
          ) : (
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!allAnswered || submitting}
              className="cta-button"
              data-testid="assessment-submit"
              style={{ opacity: !allAnswered || submitting ? 0.5 : 1 }}
            >
              {submitting ? "Submitting…" : "Submit my answers"}
            </button>
          )}
        </div>
      </div>
    </Centered>
  );
}
